import { useTranslation } from 'react-i18next';
import { Box, styled, useTheme } from '@mui/material';

import { Typography } from '@zendesk/forethought-elements';
import { ArticleTranscriptComponent } from '../ConversationTranscript/types';
import { AddKnowledgeMenuButton } from './AddKnowledgeMenuButton';
import { ArticlesSectionFromTranscriptComponents } from './ArticlesSectionFromTranscriptComponent';
import { quickFeedbackOneToTwoOptions } from 'src/pages/solve-config/csatStyleOptions';
import { QuickFeedbackRecord } from 'src/services/apiInterfaces';
import { useGetFeatureFlagsQuery } from 'src/services/dashboard-api/dashboardApi';

export const ArticlesSection = ({
  onCreateNewInstruction,
  quickFeedbackResponses,
  transcriptComponents,
}: {
  onCreateNewInstruction: (newId: string) => void;
  quickFeedbackResponses?: Array<QuickFeedbackRecord> | null;
  transcriptComponents?: ArticleTranscriptComponent[] | null;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const { data: featureFlagsData } = useGetFeatureFlagsQuery();
  const { feature_flags: featureFlags = [] } = featureFlagsData ?? {};
  const isQuickFeedbackEnabled = featureFlags.includes('quick_feedback');

  // if transcript component is defined, show the revamped articles section
  if (transcriptComponents) {
    return (
      <ArticlesSectionFromTranscriptComponents
        onCreateNewInstruction={onCreateNewInstruction}
        transcriptComponents={transcriptComponents}
      />
    );
  }

  if (
    !isQuickFeedbackEnabled ||
    !quickFeedbackResponses ||
    quickFeedbackResponses.length === 0
  ) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
      }}
    >
      <Divider />
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography color={palette.colors.grey[600]} variant='font12Medium'>
          {t('articlesSection.articles')}
        </Typography>
        <AddKnowledgeMenuButton onSuccess={onCreateNewInstruction} />
      </Box>
      {quickFeedbackResponses.map((quickFeedbackRecord, index) => {
        const chosenStyleOptions = quickFeedbackOneToTwoOptions.find(
          option => option.value === quickFeedbackRecord.style,
        );

        return (
          <Box
            key={index}
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: '4px',
            }}
          >
            <Typography variant='font12'>
              <QuickFeedbackArticleLink
                href={quickFeedbackRecord.link}
                rel='noreferrer'
                target='_blank'
              >
                {quickFeedbackRecord.title}
              </QuickFeedbackArticleLink>
            </Typography>
            {quickFeedbackRecord.is_useful !== null && (
              <QuickFeedbackStyleImage
                src={
                  chosenStyleOptions?.[
                    `${
                      quickFeedbackRecord.is_useful ? 'positive' : 'negative'
                    }Icons`
                  ]?.active
                }
              />
            )}
            {quickFeedbackRecord.additional_feedback && (
              <Typography color={palette.colors.grey[600]} variant='font12'>
                <i>
                  {t('common.quotedValue', {
                    value: quickFeedbackRecord.additional_feedback,
                  })}
                </i>
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

const QuickFeedbackStyleImage = styled('img')`
  height: 18px;
  width: 18px;
`;

const QuickFeedbackArticleLink = styled('a')`
  color: ${({ theme }) => theme.palette.colors.blue[600]};
`;

const Divider = styled('hr')`
  border-width: 0;
  border-bottom-width: thin;
  border-color: ${({ theme }) => theme.palette.colors.slate[200]};
  width: 100%;
  margin-top: 8px;
  margin-bottom: 8px;
`;
